import { ECode } from '@libs/shared';

/**
 * 用量超限相关错误码
 * 升级/增购 提示场景中会提示用户当前版本的该功能已经超量
 */
export const VIP_EXCEED_ERROR = {
    /** 应用数量 */
    APP: 2067,
    /** 我方对接人数 */
    CORP_COOP_DOCKER: ECode.CORP_COOP_GLOBAL_DOCKER_COUNT_EXCEEDED,
    /** 互联组织数量 */
    CORP_COOP_LIMIT: ECode.CORP_COOP_EXCEEDED
};

/**
 * 版本相关错误码（如果当前产品版本没有该功能则报错）
 * 升级/增购 提示场景中会提示用户当前版本不包含该功能
 * number：后端错误码
 * symbol：纯前端错误码标识，保证唯一性即可
 */
export const VIP_VERSION_ERROR = {
    /** 成员自动同步 */
    USER_SYNC: ECode.INTEGRATE_AUTO_SYNC_UNAVAILABLE,
    /** 单点登录 */
    SSO: 7141,
    /** 企业风格 */
    CORP_THEME: ECode.CUSTOM_CORP_THEME_UNSUPPORTED,
    /** 开放平台（fx-code 仍返回 17008） */
    OPEN_PLATFORM: 17008,
    /** 兼容平台接口返回的 ECode */
    OPEN_PLATFORM_E: ECode.OPEN_PLATFORM_UNAVAILABLE,
    /** 开放 API */
    OPEN_API: 17011,
    /** 互联组织功能 */
    CORP_COOP: ECode.CORP_COOP_NOT_IN_PLAN,
    /** 自定义登录页 */
    CUSTOM_LOGIN: ECode.SSO_CONFIG_UNSUPPORTED,
    // 授权管理 - 通讯录成员启用、停用
    CONTACT_MEMBER_STOP: Symbol('contact_member_stop'),
    // 自建插件
    PRIVATE_PLUGIN: Symbol('private_plugin'),
    // 系统水印
    GLOBAL_WATERMARK: Symbol('global_watermark'),
    /** 高管模式（提醒屏蔽） */
    CORP_EXECUTIVE: Symbol('corp_executive'),
    /** 集成应用 */
    OPEN_APPLICATION: ECode.OPEN_APPLICATION_FEATURE_UNAVAILABLE,
    /** 成员api */
    MEMBER_API: ECode.MEMBER_API_UNSUPPORTED
};

/**
 * 高级功能相关错误码
 */
export const VIP_ERROR = {
    ...VIP_EXCEED_ERROR,
    ...VIP_VERSION_ERROR
};

/**
 * 获取vip相关的error code
 */
export const VIP_ERROR_CODE = [
    VIP_EXCEED_ERROR.CORP_COOP_DOCKER,
    7116,
    7117,
    7118,
    7119,
    VIP_VERSION_ERROR.USER_SYNC,
    VIP_VERSION_ERROR.SSO,
    VIP_VERSION_ERROR.OPEN_PLATFORM,
    VIP_VERSION_ERROR.CORP_COOP
];

/**
 * 结算货币
 */
export const CURRENCY_SIGN = {
    // 美元 $
    usd: '$',
    // 人民币 ¥
    cny: '¥'
};

export const RECEIPT_TYPE = {
    // 提前开票
    ADVANCE: 'advance',
    // 正常开票
    NORMAL: 'normal'
};

// 订单类型
export const ORDER_TYPE = {
    COIN: 'coin',
    PLUGIN: 'plugin',
    DATA_SOURCE: 'datasource',
    PLATFORM_DATA_SOURCE: 'platform_datasource',
    UPGRADE: 'upgrade',
    RENEW: 'renew',
    AMOUNT_ADDON: 'amountAddon'
};

export const RECEIPT_STATE = {
    // 已作废
    INVALID: -2,
    // 未开票
    UNRECEIPT: 0,
    // 开票中
    RECEIPTING: 1,
    // 已开票
    RECEIPTED: 2
};

// 开票内容
export const RECEIPT_CONTENT_TYPE = {
    app_platform: {
        value: 'app_platform',
        text: 'receipt.content.appPlatform',
        taxRate: 'receipt.tax',
        tax: '16%'
    },
    tech_service: {
        value: 'tech_service',
        text: 'receipt.content.techService',
        taxRate: 'receipt.tax',
        tax: '6%'
    }
};

// 电子类型发票
export const ELECTRIC_RECEIPT_TYPE = ['e_plain', 'e_special', 'e_personal'];

// 开票类型
export const RECEIPT_TYPE_MAP = {
    e_plain: 'receipt.type.electric.plain',
    e_special: 'receipt.type.electric.special',
    e_personal: 'receipt.type.electric.personal',

    // ------以下纸质发票类型废弃, 只做历史兼容处理.
    plain: 'receipt.type.plain',
    special: 'receipt.type.special',
    personal: 'receipt.type.personal'
};

// 钉钉支付模式
export const DING_PAY_METHOD = 'dingtalk';

export const ORDER_STATE = {
    // 已取消
    CANCELED: -1,
    // 未支付
    UNPAID: 0,
    // 已支付
    PAID: 1
};

export const DELIVERY_STATE = {
    INVALID: -2,
    DENY: -1,
    WAIT: 0,
    ACCESS: 1,
    REBILLING: 2
};

/**
 * VIP订单
 */
export const VIP_ORDER = {
    PACK: {
        free: {
            name: 'vip.pack.free'
        },
        lite: {
            name: 'vip.pack.lite'
        },
        std: {
            name: 'vip.pack.std'
        },
        user_std: {
            name: 'vip.pack.std'
        },
        jodoo_user_std: {
            name: 'vip.pack.std'
        },
        jodoo_starter: {
            name: 'vip.pack.starter'
        },
        user: {
            name: 'vip.pack.user'
        },
        pro: {
            name: 'vip.pack.pro'
        },
        ent: {
            name: 'vip.pack.ent'
        },
        logo: {
            name: 'vip.pack.logo'
        },
        oem: {
            name: 'vip.pack.oem'
        },
        user_ent: {
            name: 'vip.pack.ent'
        },
        jodoo_user_ent: {
            name: 'vip.pack.ent'
        },
        trial: {
            name: 'vip.pack.trial'
        },
        self_hosted: {
            name: 'vip.pack.local'
        },
        user_premium: {
            name: 'vip.pack.premium'
        },
        starter: {
            name: 'vip.pack.starter'
        }
    },
    SERVICE: {
        basic: {
            name: 'vip.service.basic.name'
        },
        silver: {
            name: 'vip.service.advanced.name'
        },
        gold: {
            name: 'vip.service.advanced.name'
        }
    },
    // 数据显示单位
    DATA_UNIT: 10000,
    // 数据增购单位
    DATA_ADD_UNIT: 100000,
    // 单表数据增购单位
    FORM_DATA_UNIT: 10000,
    // 人数增购单位
    USER_ADD_UNIT: 5,
    // 上传量增购单位
    UPLOAD_ADD_UNIT: 1024 * 1024 * 1024,
    // 数据回收站增购单位
    DATA_BACKUP_UNIT: 15
};

/** 支付完成页仍使用订单明细格式化逻辑的订单类型 */
export const LEGACY_ORDER_TYPE = [
    ORDER_TYPE.PLUGIN,
    ORDER_TYPE.COIN,
    ORDER_TYPE.DATA_SOURCE,
    ORDER_TYPE.PLATFORM_DATA_SOURCE
];
